import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { AlertTriangle, Brain, ClipboardCheck, Eye, Scale } from "lucide-react";

export default function AiDisclaimer() {
  const sections = [
    {
      title: "AI Can Be Wrong",
      icon: AlertTriangle,
      items: [
        "Scores and rankings may be affected by CV formatting, missing text extraction, ambiguous job descriptions, or incomplete candidate information.",
        "AI summaries may miss relevant experience or overstate alignment when the source CV is unclear.",
        "Skill matching can fail when candidates use uncommon names, abbreviations, old technologies, or translated terms.",
      ],
    },
    {
      title: "Decision Support Only",
      icon: Brain,
      items: [
        "RecruitGlance helps recruiters compare evidence faster, but it is not a replacement for human review.",
        "Recruiters should read the original CV and compare it with the job description before taking action.",
        "The final hiring decision must remain with the recruiter or hiring team.",
        "RecruitGlance should not be used as a fully automated hiring, rejection, interview, compensation, or employment eligibility decision system.",
      ],
    },
    {
      title: "EU AI Act And High-Risk Use",
      icon: Scale,
      items: [
        "AI systems used for recruitment, candidate ranking, job matching, or selection may be treated as high-risk systems under the EU AI Act.",
        "Customers using RecruitGlance in the EU should maintain human oversight, use clear job-relevant criteria, monitor results for bias, and keep appropriate recruitment records.",
        "RecruitGlance provides explainable candidate evidence such as matched skills, gaps, scoring signals, AI summaries, interview questions, and ranking reasons to support accountable human review.",
      ],
    },
    {
      title: "Fairness And Compliance",
      icon: Scale,
      items: [
        "Do not use AI output as the only reason to reject, shortlist, interview, or hire a candidate.",
        "Review results for possible bias and follow applicable employment, privacy, and anti-discrimination rules.",
        "Use job-relevant criteria and avoid uploading information that is not needed for recruitment.",
        "Do not ask RecruitGlance to evaluate protected traits or use outputs to discriminate against candidates based on legally protected characteristics.",
      ],
    },
    {
      title: "Recruiter Review",
      icon: Eye,
      items: [
        "Check matched skills, missing skills, titles held, AI summary, and ranking reason against the original CV.",
        "Delete wrong imports, bills, non-CV attachments, or records that should not be stored.",
        "Use notes and shortlist status as recruiter decisions, not as automatic AI decisions.",
      ],
    },
    {
      title: "Practical Use",
      icon: ClipboardCheck,
      items: [
        "Use the ranking to prioritize review order, not to finalize hiring decisions automatically.",
        "Use AI summaries to speed up reading, then verify important claims manually.",
        "When in doubt, open the candidate CV and review the source content directly.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Navbar />

      <main className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-8 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-amber-800">
              RecruitGlance AI Disclaimer
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              AI results are helpful, but not final decisions.
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-amber-950">
              RecruitGlance uses AI to summarize, rank, and explain candidate fit.
              Those outputs can be inaccurate or incomplete, so recruiters must
              verify the original CV before making employment decisions.
            </p>
            <p className="mt-4 text-sm text-amber-800">
              Last updated: July 17, 2026
            </p>
          </div>

          <div className="mt-8 grid gap-5">
            {sections.map(({ title, icon: Icon, items }) => (
              <section key={title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 text-white">
                    <Icon size={20} />
                  </span>
                  <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
                </div>
                <ul className="mt-5 space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/" className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900">
              Back to Home
            </Link>
            <Link to="/terms" className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 transition hover:bg-slate-100">
              Terms of Use
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
